<template>
  <template v-if="!error">
    <div class="convenciones-container">
      <template v-if="isLoading">
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">Cargando convenciones...</div>
        </div>
      </template>

      <template v-else>
        <template v-if="conventions.length > 0">
          <div v-for="(convention, index) in conventions" :key="convention.id" class="convention-block">
            <!-- Header con título y enlace -->
            <div class="convention-header">
              <div class="header-left">
                <h2 class="convention-title">{{ convention.name }}</h2>
                <!-- Botón "Ver agenda" oculto temporalmente: este segmento se usa para capacitaciones/cursos. Poner SHOW_AGENDA_BUTTON en true para re-habilitarlo. -->
                <button v-if="SHOW_AGENDA_BUTTON" class="ver-agenda-link" @click="() => openScheduleModal(convention)">Ver agenda</button>
              </div>
              <div class="header-right">
                <div class="info-label">
                  <span class="label-text">FECHA: {{ formatDate(convention.start_datetime) }}</span>
                </div>
                <div class="info-label">
                  <span class="label-text">LUGAR: {{ convention.place || "CORDOBA" }}</span>
                </div>
              </div>
            </div>
            <div class="conferencias-list">
              <template v-if="isAuthenticated">
                <div v-for="(
conference, conferenceIndex
                  ) in getDisplayedConferences(convention.id)" :key="conference.id" class="conference-item">
                  <ConferenceComponent :conference="conference" :primary-color="conference.primary_color"
                    :secondary-color="conference.secondary_color" :audience-target="conference.audience_target"
                    :conference-id="conference.id" :index="conferenceIndex"
                    :registration-end-date="convention.registration_end_date" />
                </div>
              </template>
            </div>

            <!-- Botón Cargar más -->
            <div v-if="hasMoreConferences(convention.id)" class="load-more-container">
              <div class="conferences-counter">
                Mostrando {{ getDisplayedConferences(convention.id).length }} de {{ convention.conferences?.length || 0
                }}
                conferencias
              </div>
              <button class="btn-cargar-mas" @click="() => loadMoreConferences(convention.id)">
                Cargar más
              </button>
            </div>

            <div v-if="index < conventions.length - 1" class="convention-divider"></div>
          </div>
        </template>

        <template v-else>
          <div class="no-conventions">No hay convenciones disponibles.</div>
        </template>
      </template>
    </div>

    <!-- Modal de cronograma -->
    <ScheduleModal v-if="isScheduleModalVisible && currentConvention" :is-visible="isScheduleModalVisible"
      :conferences="allConferences" :convention="currentConvention" :convention-date="currentConventionDate"
      :convention-place="currentConventionPlace" @close="closeScheduleModal" />
  </template>
</template>
<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import conventionService from "@/services/api/convention";
import ConferenceComponent from "@/views/servicios/components/socios/convencion/ConferenceComponent.vue";
import ScheduleModal from "@/views/servicios/components/socios/convencion/ScheduleModal.vue";

const store = useStore();
const isLoading = ref(true);
const error = ref(null);

const isAuthenticated = computed(() => !!store.state.user);

const conventions = ref([]);
const displayedConferences = ref({}); // Objeto que almacena las conferencias mostradas por cada convención
const conferencesPerPage = 6;
const currentPage = ref({}); // Objeto que almacena la página actual por cada convención

// Variables para el modal de cronograma
const isScheduleModalVisible = ref(false);
const selectedConvention = ref(null);

// Visibilidad del botón "Ver agenda": oculto temporalmente porque este
// segmento se usa para capacitaciones/cursos. Cambiar a true para re-habilitarlo.
const SHOW_AGENDA_BUTTON = false;

const concessionaireId = computed(
  () => store.getters.getSelectedConcessionaire?.id
);

const fetchConventions = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    if (!concessionaireId.value) {
      throw new Error("No se ha seleccionado un concesionario.");
    }

    const response = await conventionService.getConventionInformation(
      concessionaireId.value
    );

    conventions.value = response.data;

    // Inicializar las conferencias mostradas para cada convención
    conventions.value.forEach((convention) => {
      const allConferences = convention.conferences || [];
      displayedConferences.value[convention.id] = allConferences.slice(0, conferencesPerPage);
      currentPage.value[convention.id] = 1;
    });
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      err.message ||
      "Error al cargar las convenciones";
    console.error("Error fetching conventions:", err);
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "No especificado";

  const date = new Date(dateString);
  if (isNaN(date)) return "Fecha inválida";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const getDisplayedConferences = (conventionId) => {
  return displayedConferences.value[conventionId] || [];
};

const loadMoreConferences = (conventionId) => {
  const convention = conventions.value.find(c => c.id === conventionId);
  if (!convention) return;

  const allConferences = convention.conferences || [];
  const currentPageForConvention = currentPage.value[conventionId] || 1;
  const nextPage = currentPageForConvention + 1;
  const startIndex = (nextPage - 1) * conferencesPerPage;
  const endIndex = startIndex + conferencesPerPage;

  const newConferences = allConferences.slice(startIndex, endIndex);

  if (newConferences.length > 0) {
    displayedConferences.value[conventionId] = [
      ...(displayedConferences.value[conventionId] || []),
      ...newConferences
    ];
    currentPage.value[conventionId] = nextPage;
  }
};

const hasMoreConferences = (conventionId) => {
  const convention = conventions.value.find(c => c.id === conventionId);
  if (!convention) return false;

  const allConferences = convention.conferences || [];
  const displayed = displayedConferences.value[conventionId] || [];
  return displayed.length < allConferences.length;
};

// Computed properties para el modal de cronograma
const allConferences = computed(() => {
  if (selectedConvention.value) {
    return selectedConvention.value.conferences || [];
  }
  return [];
});

const currentConventionDate = computed(() => {
  if (selectedConvention.value) {
    return formatDate(selectedConvention.value.start_datetime);
  }
  return "18-08-2025";
});

const currentConventionPlace = computed(() => {
  if (selectedConvention.value) {
    return selectedConvention.value.place || "CÓRDOBA";
  }
  return "CÓRDOBA";
});

const currentConvention = computed(() => {
  return selectedConvention.value || null;
});

watch(concessionaireId, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    fetchConventions();
  }
});

// Funciones para el modal de cronograma
const openScheduleModal = (convention) => {
  selectedConvention.value = convention;
  isScheduleModalVisible.value = true;
};

const closeScheduleModal = () => {
  isScheduleModalVisible.value = false;
  selectedConvention.value = null;
};

onMounted(fetchConventions);
</script>

<style scoped>
.convenciones-container {
  padding: 1rem;
  margin: 3rem 0 1rem 0;
}

h2 {
  color: #021f5b;
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-align: center;
  letter-spacing: 1.5px;
}

.convencion-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #afafaf05;
  border-radius: 6px;
  max-width: 600px;
  margin: 0 auto;
}

.convencion-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.label {
  font-weight: 600;
  color: #021f5b;
  font-size: 1.5rem;
}

.value {
  color: #7f8c8d;
  font-size: 1.4rem;
  font-weight: 500;
}

@media (max-width: 992px) {
  h2 {
    font-size: 2.2rem;
    letter-spacing: 1px;
  }

  .label {
    font-size: 1.3rem;
  }

  .value {
    font-size: 1.25rem;
  }
}

@media (max-width: 768px) {
  .conference-item:not(:last-child)::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(90deg,
        rgba(200, 200, 200, 0) 0%,
        rgba(163, 163, 163, 0.5) 50%,
        rgba(200, 200, 200, 0) 100%);
    margin: 0.75rem 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .conference-item:not(:last-child) {
    margin-bottom: 1rem;
    border: solid;
  }

  h2 {
    font-size: 2rem;
  }

  .convencion-info {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .label,
  .value {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  h2 {
    font-size: 1.75rem;
  }

  .label,
  .value {
    font-size: 1.1rem;
  }

  .convencion-item {
    padding: 0.75rem;
    gap: 0.75rem;
  }

}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1rem;
  animation: fadeIn 0.5s ease-in;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .loading-text {
    font-size: 1rem;
  }
}

@media (max-width: 400px) {
  .convenciones-container {
    padding: 0.5rem;
  }

  h2 {
    font-size: 1.5rem;
    line-height: 1.3;
    margin-bottom: 0.75rem;
  }

  .convencion-item {
    width: 100%;
    padding: 0.5rem;
    gap: 0.75rem;
  }

  .convencion-info {
    gap: 0.25rem;
    width: 100%;
  }

  .label,
  .value {
    font-size: 1rem !important;
    line-height: 1.4;
  }

  .loading-spinner {
    width: 30px;
    height: 30px;
    border-width: 2px;
  }

  .loading-text {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) and (orientation: landscape) {
  .convenciones-container {
    padding: 0.5rem;
  }

  h2 {
    font-size: 1.25rem;
  }
}

.convencion-info {
  flex-wrap: wrap;
}

.value {
  word-break: break-word;
  /* Evitar overflow de texto */
}

.conferencias-list {
  margin: 2rem auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 1400px;
  padding: 0 1rem;
}

@media (max-width: 1200px) {
  .convention-header {
    max-width: 900px;
  }

  .conferencias-list {
    grid-template-columns: repeat(2, 1fr);
    max-width: 900px;
  }
}

@media (max-width: 992px) {
  .convention-header {
    max-width: 500px;
  }

  .conferencias-list {
    grid-template-columns: 1fr;
    max-width: 500px;
  }
}

.conference-item {
  transition: opacity 0.3s ease;
}

.load-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
}

.conferences-counter {
  color: #666;
  font-size: 0.9rem;
  text-align: center;
}

.btn-cargar-mas {
  background: #ffffff;
  color: #021f5b;
  border: 2px solid #3498db;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cargar-mas:hover {
  background: #f8f9fa;
  border-color: #2980b9;
}

.schedule-image-container {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  overflow: hidden;
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.full-width-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  padding: 1.6rem 1.6rem 0 1.6rem;
}

.convenciones-container {
  padding: 1rem;
  margin: 3rem 0 1rem 0;
}

.convention-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;
}

.convention-title {
  color: #021f5b;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 1.5px;
  font-family: sans-serif;
}

.ver-agenda-link {
  background: #016fd5;
  color: white;
  border: none;
  padding: 1.25rem 2.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  box-shadow: 0 4px 12px rgba(1, 111, 213, 0.4);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.ver-agenda-link:hover {
  background: #015bb0;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(1, 111, 213, 0.5);
}

.info-label {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  text-align: center;
}

.label-text {
  color: #495057;
  font-size: 1rem;
  font-weight: 500;
}

.header-right {
  display: flex;
  gap: 1rem;
}

@media (max-width: 992px) {
  .convention-title {
    font-size: 2.2rem;
    letter-spacing: 1px;
  }
}

@media (max-width: 768px) {
  .convention-header {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
    text-align: center;
  }

  .convention-title {
    font-size: 2rem;
  }

  .header-right {
    flex-direction: row;
    gap: 1rem;
  }

  .info-label {
    padding: 0.4rem 0.8rem;
  }

  .label-text {
    font-size: 0.9rem;
  }
}
</style>